import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MyListApi } from "../utils/restApi";
import { ImageProperty } from "./ImageProperty";
import { LocationProperty } from "./LocationProperty";
import { EnumProperty } from "./property-updates/EnumProperty";
import { useDispatch } from "react-redux";
import { addNotification } from "../slices/notificationSlice";
import { EnumArrayField } from "./items/EnumArrayField";

export const CreateItemUpdate = (props) => {
  const dispatch = useDispatch();
  const { itemId, property } = useParams();
  const [item, setItem] = useState(null);
  const [value, setValue] = useState(null);
  const [action, setAction] = useState("update");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!item) {
      MyListApi.getItem(itemId)
        .then((data) => setItem(data))
        .catch((error) => {});
    }
  }, [item, itemId]);

  const dataInfo = ((i) => {
    if (item) {
      const infos = JSON.parse(i.tag.dataInfo);
      return infos.find((i) => i.name === property);
    }
    return null;
  })(item);

  const itemData = item ? JSON.parse(item.data || "{}") : {};
  const currentValue = itemData?.[property];
  const currentValues = Array.isArray(currentValue) ? currentValue : [];
  const selectedValues = Array.isArray(value) ? value : [];
  const dataInfoName = dataInfo?.name;
  const isArrayProperty = dataInfo?.type?.startsWith("array") ?? false;
  const dataInfoValues = dataInfo?.options?.values || dataInfo?.values || [];
  const availableValues =
    action === "remove" ? currentValues : dataInfoValues.filter((v) => !currentValues.includes(v));

  const filteredDataInfo =
    dataInfo && isArrayProperty
      ? {
          ...dataInfo,
          options: {
            ...(dataInfo.options || {}),
            values: availableValues,
          },
        }
      : dataInfo;

  useEffect(() => {
    if (!dataInfoName) return;

    setAction(isArrayProperty ? "add" : "update");
    setValue(isArrayProperty ? [] : null);
  }, [dataInfoName, isArrayProperty]);

  const handleSave = (e) => {
    e.preventDefault();
    setLoading(true);
    MyListApi.createItemUpdate(itemId, property, value, action)
      .then((data) => {
        navigate(`/items/${itemId}`);
      })
      .catch((error) => {
        dispatch(
          addNotification({
            message: error.message,
            type: "error",
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChange = (val) => {
    setValue(val);
  };

  const displayValue = (val) => {
    if (val == null || val === "") return "\u00A0";
    if (typeof val === "object") return JSON.stringify(val);
    return val;
  };

  const currentValueElement = isArrayProperty ? (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
      {currentValues.map((v) => {
        const isSelectedForRemoval = action === "remove" && selectedValues.includes(v);
        return <Chip key={`current-${v}`} label={v} color={isSelectedForRemoval ? "error" : "default"} />;
      })}
      {action === "add" ? selectedValues.map((v) => <Chip key={`add-${v}`} label={v} color="success" />) : null}
      {currentValues.length === 0 && selectedValues.length === 0 ? <Typography>{"\u00A0"}</Typography> : null}
    </Box>
  ) : (
    <Typography>{displayValue(currentValue)}</Typography>
  );

  const inputElement = {
    image: <ImageProperty label={property} value={value} onChange={handleChange} />,
    location: <LocationProperty label={property} value={value} onChange={handleChange} />,
    enum: (
      <EnumProperty label={property} value={value} onChange={(e) => handleChange(e.target.value)} dataInfo={dataInfo} />
    ),
    array_enum: (
      <EnumArrayField
        label={property}
        value={value || []}
        onChange={(e) => handleChange(e.target.value)}
        dataInfo={filteredDataInfo}
      />
    ),
  };

  return (
    <Paper sx={{ padding: "8px" }}>
      <Backdrop open={loading}>
        <CircularProgress />
      </Backdrop>
      <Box component="form" onSubmit={handleSave} sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Typography>Create Item Update</Typography>
        {item ? (
          <>
            <Typography>{item.tag.name}</Typography>
            <Typography>{item.name}</Typography>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {property}
              </Typography>
              {currentValueElement}
            </Box>
            {inputElement[`${dataInfo?.type}`] || (
              <TextField label={property} value={value || ""} onChange={(e) => handleChange(e.target.value)} />
            )}
          </>
        ) : null}
        {isArrayProperty ? (
          <Box>
            <ToggleButtonGroup
              exclusive
              value={action}
              onChange={(_, value) => {
                if (!value) return;
                setAction(value);
                setValue([]);
              }}
            >
              <ToggleButton value="add">Add</ToggleButton>
              <ToggleButton value="remove">Remove</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        ) : null}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button component={Link} to={`/items/${itemId}`} variant="contained" color="error">
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            Save
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
